/**
 * normaliseApi.ts
 * ──────────────
 * Centralized response normalisers for ALL backend functions that return
 * wrapped shapes instead of plain arrays.
 *
 * RULE: Every time you call one of these functions, pipe the raw response
 * through the matching normaliser. NEVER unwrap inline — always add a
 * normaliser here instead.
 *
 * Shape map (as of 2026-04-06):
 *   milsimAars         → { aars: [] }
 *   loa                → { loas: [] }
 *   milsimConductReport→ { reports: [] }
 *   milsimSitrep       → { sitreps: [] }
 *   milsimLace         → { lace_reports: [] }
 *   milsimBriefings    → { briefings: [] }
 *   campaigns          → { campaigns: [] }  (or NOT_PRO error object)
 *   milsimGroups mine  → { groups: [], roster: [] }
 *   milsimGroupsB mine/own → { groups: [], roster: [] }
 *
 * Functions that return plain arrays (no normaliser needed):
 *   milsimOps, milsimRosters, dutyRoster, milsimTrainingReviews,
 *   milsimGroupsB/intel-docs, milsimGroupsB/salute-reports,
 *   milsimGroupsB/threat-profiles, roleFitnessReviews, siteSettings
 */

// ── AAR ──────────────────────────────────────────────────────────────────────
/** milsimAars → { aars: [] }  →  AarRecord[] */
export function normaliseAars(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.aars ?? r.items ?? [];
  }
  return [];
}

// ── LOA ──────────────────────────────────────────────────────────────────────
/** loa → { loas: [] }  →  LoaRecord[] */
export function normaliseLoas(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.loas ?? r.items ?? [];
  }
  return [];
}

// ── CONDUCT REPORTS ──────────────────────────────────────────────────────────
/** milsimConductReport → { reports: [] }  →  ConductRecord[] */
export function normaliseConductReports(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.reports ?? r.items ?? [];
  }
  return [];
}

// ── SITREPS ──────────────────────────────────────────────────────────────────
/** milsimSitrep → { sitreps: [] }  →  SitrepRecord[] */
export function normaliseSitreps(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.sitreps ?? r.items ?? [];
  }
  return [];
}

// ── LACE REPORTS ─────────────────────────────────────────────────────────────
/** milsimLace → { lace_reports: [] }  →  LaceRecord[] */
export function normaliseLaceReports(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.lace_reports ?? r.laceReports ?? r.items ?? [];
  }
  return [];
}

// ── BRIEFINGS ────────────────────────────────────────────────────────────────
/** milsimBriefings → { briefings: [] }  →  BriefingRecord[] */
export function normaliseBriefings(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.briefings ?? r.items ?? [];
  }
  return [];
}

// ── CAMPAIGNS ────────────────────────────────────────────────────────────────
/** campaigns → { campaigns: [] } OR { error, code: 'NOT_PRO' }  →  CampaignRecord[] */
export function normaliseCampaigns(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    if (r.code === 'NOT_PRO') return []; // not an error — just not Pro
    return r.campaigns ?? r.items ?? [];
  }
  return [];
}

// ── OPS (milsimOps returns plain array, but in some routes returns obj) ───────
/** milsimOps → usually plain array, but some routes return { ops: [] } */
export function normaliseOps(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.ops ?? r.items ?? [];
  }
  return [];
}

// ── APPLICATIONS ─────────────────────────────────────────────────────────────
/** milsimApplications → { applications: [] } */
export function normaliseApplications(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    return r.applications ?? r.items ?? [];
  }
  return [];
}

// ── MINE/OWN (milsimGroups or milsimGroupsB) ──────────────────────────────────
/** Returns { groups: [], roster: [] } */
export function normaliseMineOwn(raw: unknown): { groups: any[]; roster: any[] } {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const r = raw as Record<string, any>;
    return {
      groups: Array.isArray(r.groups) ? r.groups : [],
      roster: Array.isArray(r.roster) ? r.roster : [],
    };
  }
  return { groups: [], roster: [] };
}

// ── GENERIC FALLBACK ─────────────────────────────────────────────────────────
/**
 * Use when you know a function returns either a plain array OR a wrapped
 * object but you don't know the wrapper key. Tries common keys.
 * Prefer a specific normaliser above when possible.
 */
export function normaliseToArray(raw: unknown, keys?: string[]): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, any>;
    const tryKeys = keys ?? ['items', 'data', 'results', 'records'];
    for (const k of tryKeys) {
      if (Array.isArray(r[k])) return r[k];
    }
    // Last resort: first array value
    const firstArray = Object.values(r).find(Array.isArray);
    if (firstArray) return firstArray as any[];
  }
  return [];
}
